import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';

import DatePicker from 'components/DatePicker';
import MobileFilterSheet from 'components/Layout/MobileShell/MobileFilterSheet';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import { ListedProduct } from 'helper/products';
import { CompanyModalFilters } from 'helper/companyFilters';
import {
  onCheckChange,
  onDatePickerChange,
  onExclusiveCheckChange,
} from 'helper/controlledField';
import {
  loadCompanyFilters,
  onCompanyFiltersClear,
  onCompanyFiltersSave,
  onProductCheckChange,
} from 'helper/filterModals';

import './index.scss';

type FilterCategoryKey = 'produto' | 'vigencia' | 'status' | 'categoria';

interface FilterSheetProps {
  open: boolean,
  closeModal: () => void,
  filters: CompanyModalFilters,
  setFilters: React.Dispatch<React.SetStateAction<CompanyModalFilters>>,
  productsLoading: boolean,
  products: ListedProduct[] | undefined,
}

export default function FilterSheet({
  open,
  closeModal,
  filters,
  setFilters,
  productsLoading,
  products,
}: FilterSheetProps) {
  const [selectedProducts, setSelectedProducts] = useState([] as number[]);
  const [contractEndsBefore, setContractEndsBefore] = useState<Date | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [isInactive, setIsInactive] = useState(false);
  const [isTest, setIsTest] = useState(false);
  const [isExpiringIn30, setIsExpiringIn30] = useState(false);
  const [isExpiringIn60, setIsExpiringIn60] = useState(false);
  const [isMetereology, setIsMetereology] = useState(false);
  const [isConsultancy, setIsConsultancy] = useState(false);
  const [openCategory, setOpenCategory] = useState<FilterCategoryKey | ''>('');

  const statusCount = [isActive, isInactive, isTest, isExpiringIn30, isExpiringIn60]
    .filter(Boolean).length;
  const categoryText = (isMetereology && 'Meteorologia') || (isConsultancy && 'Consultoria')
    || undefined;

  useEffect(() => {
    if (open) {
      loadCompanyFilters(
        filters,
        setSelectedProducts,
        setContractEndsBefore,
        setIsActive,
        setIsInactive,
        setIsTest,
        setIsExpiringIn30,
        setIsExpiringIn60,
        setIsMetereology,
        setIsConsultancy,
      );
      setOpenCategory('');
    }
  }, [open, filters]);

  const toggleCategory = (category: FilterCategoryKey) => () => {
    setOpenCategory((current) => (current === category ? '' : category));
  };

  const clearDraft = onCompanyFiltersClear(
    setSelectedProducts,
    setContractEndsBefore,
    setIsActive,
    setIsInactive,
    setIsTest,
    setIsExpiringIn30,
    setIsExpiringIn60,
    setIsMetereology,
    setIsConsultancy,
  );

  const saveDraft = onCompanyFiltersSave(
    {
      selectedProducts,
      contractEndsBefore,
      isActive,
      isInactive,
      isTest,
      isExpiringIn30,
      isExpiringIn60,
      isMetereology,
      isConsultancy,
    },
    setFilters,
    closeModal,
  );

  return (
    <MobileFilterSheet
      open={open}
      dismiss={closeModal}
      title="Filtrar empresas"
      className="companies-filter-sheet"
      onClear={clearDraft}
      onApply={saveDraft}
    >
      <Section
        label="Produto"
        valueText={selectedProducts.length ? String(selectedProducts.length) : undefined}
        open={openCategory === 'produto'}
        onToggle={toggleCategory('produto')}
      >
        {productsLoading && <Spinner animation="border" size="sm" />}
        {!productsLoading && products && products.map((product) => (
          <OptionRow
            key={product.id}
            label={product.nome}
            selectionType="multiple"
            selected={selectedProducts.includes(product.id)}
            onSelect={onProductCheckChange(
              product.id,
              selectedProducts,
              setSelectedProducts,
            )}
          />
        ))}
      </Section>

      <Section
        label="Vigência"
        valueText={contractEndsBefore ? contractEndsBefore.toLocaleDateString('pt-BR') : undefined}
        open={openCategory === 'vigencia'}
        onToggle={toggleCategory('vigencia')}
      >
        <p className="companies-filter-sheet-hint">Contratos que vencem antes de:</p>
        <div className="companies-filter-sheet-datepicker">
          <DatePicker
            inline
            selected={contractEndsBefore}
            onChange={onDatePickerChange(setContractEndsBefore)}
          />
        </div>
      </Section>

      <Section
        label="Status"
        valueText={statusCount ? String(statusCount) : undefined}
        open={openCategory === 'status'}
        onToggle={toggleCategory('status')}
      >
        <OptionRow
          label="Ativo"
          selectionType="single"
          selected={isActive}
          onSelect={onExclusiveCheckChange(isActive, isInactive, setIsActive, setIsInactive)}
        />
        <OptionRow
          label="Inativo"
          selectionType="single"
          selected={isInactive}
          onSelect={onExclusiveCheckChange(isInactive, isActive, setIsInactive, setIsActive)}
        />
        <OptionRow
          label="Teste"
          selectionType="multiple"
          selected={isTest}
          onSelect={onCheckChange(isTest, setIsTest)}
        />
        <OptionRow
          label="Vence em até 30 dias"
          selectionType="multiple"
          selected={isExpiringIn30}
          onSelect={onCheckChange(isExpiringIn30, setIsExpiringIn30)}
        />
        <OptionRow
          label="Vence entre 30 e 60 dias"
          selectionType="multiple"
          selected={isExpiringIn60}
          onSelect={onCheckChange(isExpiringIn60, setIsExpiringIn60)}
        />
      </Section>

      <Section
        label="Categoria"
        valueText={categoryText}
        open={openCategory === 'categoria'}
        onToggle={toggleCategory('categoria')}
      >
        <OptionRow
          label="Ampere Meteorologia"
          selectionType="single"
          selected={isMetereology}
          onSelect={onExclusiveCheckChange(
            isMetereology,
            isConsultancy,
            setIsMetereology,
            setIsConsultancy,
          )}
        />
        <OptionRow
          label="Ampere Consultoria"
          selectionType="single"
          selected={isConsultancy}
          onSelect={onExclusiveCheckChange(
            isConsultancy,
            isMetereology,
            setIsConsultancy,
            setIsMetereology,
          )}
        />
      </Section>
    </MobileFilterSheet>
  );
}
